import { Caption1, makeStyles, Menu, MenuDivider, MenuItem, MenuItemRadio, MenuList, MenuPopover, MenuTrigger, mergeClasses, Tab, TabList, tokens, Tooltip } from "@fluentui/react-components";
import { Board16Regular, CalendarClock16Regular, CalendarLtr16Regular, MoreHorizontal16Regular } from "@fluentui/react-icons";
import { Fragment, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { ListToolbar } from "@/components/list-toolbar";
import { todayISO } from "@/engine/date";
import { planVisibilityOf, showInPlanList, usePlanVisibilityTick } from "@/features/access/plan-visibility";
import { canWorkspace, useWorkspaceRole } from "@/features/access/workspace-access";
import { HomeGateTable } from "@/features/home/HomeGateMonth";
import { HOME_MOCK_CASES, homeMock, setHomeMockCase, useHomeMockCase, type HomeMockCase } from "@/features/home/home-mock";
import {
  buildHomeSnapshot,
  groupHomeTasks,
  homeDueScope,
  homeGates,
  homeGreeting,
  homeScale,
  type HomePlanSummaryRow,
  type HomeTaskRow,
  type HomeTaskScope,
} from "@/features/home/home-model";
import { ExecutionTaskDrawer } from "@/features/tasks/ExecutionTaskDrawer";
import { openExecutionTask } from "@/features/tasks/links";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { formatDate } from "@/lib/date-format";
import { usePageAction } from "@/shell/page-action";
import { contentShell, shellGutter } from "@/shell/content-frame";
import { UserWeeklyHomeLine } from "@/features/user-weekly/UserWeeklyPanel";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";

type HomeSection = "due" | "plans" | "gates";

function parseHomeSection(raw: string | null): HomeSection {
  if (raw === "plans") return "plans";
  if (raw === "gates") return "gates";
  return "due";
}

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}`,
    boxSizing: "border-box",
  },
  table: {
    tableLayout: "fixed",
    "& td": { height: "auto", minHeight: "34px" },
  },
  row: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  groupCell: {
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
  },
  clipCell: { overflow: "hidden", maxWidth: 0 },
  nameCol: { width: "44%" },
  planNameCol: { width: "62%" },
  planCol: { width: "32%" },
  dueCol: { width: "22%" },
  pctCol: { width: "16%" },
  late: { color: tokens.colorPaletteRedForeground1 },
  subline: {
    display: "block",
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginTop: "2px",
  },
  mockBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    flexShrink: 0,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
});

export function HomePage() {
  const styles = useStyles();
  const table = useDataTableStyles();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const section = parseHomeSection(params.get("view"));
  const projects = useCatalogStore((s) => s.projects);
  const previewGuest = useProjectStore((s) => s.previewGuest);
  const workspaceRole = useWorkspaceRole();
  const allowCreate = canWorkspace(workspaceRole, "plan.create");
  const openPlan = () => useProjectStore.getState().openDialog("create-plan");
  usePageAction(null);
  const visibilityTick = usePlanVisibilityTick();
  const guestPlanId = useProjectStore((s) => s.previewGuestPlanId);
  const me = useCatalogStore((s) => s.profile.displayName);
  const resources = useCatalogStore((s) => s.resources);
  const selfResourceId = Object.values(resources).find((resource) => resource.displayName === me)?.id;
  const activeId = useProjectStore((s) => s.activeId);
  const openProject = useProjectStore((s) => s.project);
  const [taskChoice, setTaskChoice] = useState<HomeTaskScope | null>(null);
  const today = todayISO();
  const scenario = useHomeMockCase();
  const mock = useMemo(() => (scenario === "live" ? null : homeMock(scenario, today)), [scenario, today]);
  const open = activeId && openProject.id === activeId ? openProject : null;
  const visibleProjects = previewGuest
    ? (guestPlanId && projects[guestPlanId] ? { [guestPlanId]: projects[guestPlanId] } : {})
    : Object.fromEntries(Object.entries(projects).filter(([id]) => showInPlanList(planVisibilityOf(id), workspaceRole).shown));
  void visibilityTick;
  const live = useMemo(() => buildHomeSnapshot(visibleProjects, open, me, today), [visibleProjects, open, me, today]);
  const home = mock?.snapshot ?? live;
  const dueScope = homeDueScope(taskChoice, home.dueThisWeek, home.overdue);
  const joined = mock ? mock.joined : Object.keys(visibleProjects).length;
  const noPlans = joined === 0;

  const dueGroups = useMemo(
    () => groupHomeTasks(home.tasks, today, dueScope, null, Number.POSITIVE_INFINITY),
    [home.tasks, today, dueScope],
  );

  const gates = useMemo(() => {
    const source = mock ? mock.gates : homeGates(visibleProjects, open, today);
    return [...source].sort((a, b) => a.date.localeCompare(b.date) || a.projectName.localeCompare(b.projectName, "ko") || a.title.localeCompare(b.title, "ko"));
  }, [mock, visibleProjects, open, today]);

  const openKnown = (projectId: string, go: () => void) => {
    if (scenario !== "live" && !projects[projectId]) return;
    go();
  };

  const pickMock = (next: HomeMockCase) => {
    setHomeMockCase(next);
    setTaskChoice(null);
  };

  const selectSection = (next: HomeSection) => {
    if (next === section) return;
    const updated = new URLSearchParams(params);
    if (next === "due") updated.delete("view");
    else updated.set("view", next);
    setParams(updated, { replace: true });
  };

  const showCreate = allowCreate && (noPlans || section === "plans");

  return (
    <div className={styles.page}>
      <ListToolbar
        title={homeGreeting(me)}
        description={noPlans ? undefined : homeScale(home, joined)}
        titleAside={<HomeMockMenu styles={styles} scenario={scenario} onPick={pickMock} />}
        headingAction={
          showCreate ? (
            <FuiButton appearance="primary" icon={<Board16Regular />} onClick={openPlan}>
              플랜 추가
            </FuiButton>
          ) : undefined
        }
        sections={
          noPlans ? undefined : (
            <TabList
              size="small"
              selectedValue={section}
              aria-label="홈"
              onTabSelect={(_, data) => {
                const next = data.value;
                if (next === "due" || next === "plans" || next === "gates") selectSection(next);
              }}
            >
              <Tab value="due" icon={<CalendarClock16Regular />}>마감</Tab>
              <Tab value="plans" icon={<Board16Regular />}>플랜</Tab>
              <Tab value="gates" icon={<CalendarLtr16Regular />}>일정</Tab>
            </TabList>
          )
        }
        filters={
          !noPlans && section === "due" ? (
            <SegmentedControl
              ariaLabel="마감 범위"
              value={dueScope}
              onChange={(value) => setTaskChoice(value)}
              options={[
                { value: "week", label: "이번 주" },
                { value: "overdue", label: "지연" },
              ]}
            />
          ) : undefined
        }
      />
      <div className={styles.body}>
        {noPlans ? (
          <GhostEmptyState title="아직 참여한 플랜이 없습니다" description="초대를 받거나 새 플랜을 만들어 보세요." />
        ) : section === "due" ? (
          <>
            {scenario === "live" ? (
              <UserWeeklyHomeLine
                projects={visibleProjects}
                live={open}
                actor={{ name: me, ...(selfResourceId ? { resourceId: selfResourceId } : {}) }}
                resources={resources}
              />
            ) : null}
            <DueTable
            styles={styles}
            table={table}
            groups={dueGroups.groups}
            scope={dueScope}
            overdue={home.overdue}
            dueThisWeek={home.dueThisWeek}
            onOpen={(row) => openKnown(row.projectId, () => openExecutionTask(row.projectId, row.taskId))}
          />
          </>
        ) : section === "plans" ? (
          <PlanTable styles={styles} table={table} rows={home.allPlans} onOpen={(id) => openKnown(id, () => navigate(`/projects/${id}/reports`))} />
        ) : gates.length === 0 ? (
          <GhostEmptyState title="마일스톤과 마감이 없습니다" />
        ) : (
          <HomeGateTable gates={gates} onOpen={(gate) => openKnown(gate.projectId, () => navigate(`/projects/${gate.projectId}/calendar?date=${gate.date}`))} />
        )}
      </div>
      <ExecutionTaskDrawer />
    </div>
  );
}

function HomeMockMenu({ styles, scenario, onPick }: { styles: ReturnType<typeof useStyles>; scenario: HomeMockCase; onPick: (id: HomeMockCase) => void }) {
  return (
    <Menu
      checkedValues={{ case: scenario === "live" ? [] : [scenario] }}
      onCheckedValueChange={(_, data) => {
        const next = data.checkedItems[0];
        if (next === "many" || next === "few" || next === "overdue" || next === "steady" || next === "empty") onPick(next);
      }}
      positioning="below-start"
    >
      <MenuTrigger disableButtonEnhancement>
        <button type="button" className={styles.mockBtn} aria-label="목업 사례" title="목업 사례">
          <MoreHorizontal16Regular />
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {HOME_MOCK_CASES.map((item) => (
            <MenuItemRadio key={item.id} name="case" value={item.id}>
              {item.label}
            </MenuItemRadio>
          ))}
          <MenuDivider />
          <MenuItem onClick={() => onPick("live")}>초기화</MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function DueTable({
  styles,
  table,
  groups,
  scope,
  overdue,
  dueThisWeek,
  onOpen,
}: {
  styles: ReturnType<typeof useStyles>;
  table: ReturnType<typeof useDataTableStyles>;
  groups: { key: string; label: string; rows: HomeTaskRow[] }[];
  scope: HomeTaskScope;
  overdue: number;
  dueThisWeek: number;
  onOpen: (row: HomeTaskRow) => void;
}) {
  if (groups.length === 0) {
    const title = scope === "overdue" ? "지연된 마감이 없습니다" : "이번 주 마감이 없습니다";
    const description =
      scope === "overdue"
        ? dueThisWeek > 0
          ? "이번 주에서 다가오는 마감을 볼 수 있습니다."
          : undefined
        : overdue > 0
          ? "지연에서 넘긴 마감을 볼 수 있습니다."
          : undefined;
    return <GhostEmptyState title={title} description={description} />;
  }

  const showGroup = !(groups.length === 1 && groups[0]?.key === "overdue");
  return (
    <div className={table.frame}>
      <table className={mergeClasses(table.table, styles.table)} aria-label="내 마감">
        <thead>
          <tr>
            <th className={mergeClasses(table.th, styles.nameCol)} scope="col">작업</th>
            <th className={mergeClasses(table.th, styles.planCol)} scope="col">플랜</th>
            <th className={mergeClasses(table.th, table.num, styles.dueCol)} scope="col">마감</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <Fragment key={group.key}>
              {showGroup ? (
                <tr>
                  <th className={mergeClasses(table.td, styles.groupCell)} colSpan={3} scope="colgroup">
                    {group.label}
                  </th>
                </tr>
              ) : null}
              {group.rows.map((row) => (
                <tr key={`${row.projectId}:${row.taskId}`} className={styles.row} onClick={() => onOpen(row)}>
                  <td className={mergeClasses(table.td, styles.clipCell)}>
                    <Tooltip content={row.title} relationship="description">
                      <button
                        type="button"
                        className={table.linkBtn}
                        aria-label={`${row.title}, ${row.projectName}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpen(row);
                        }}
                      >
                        {row.title}
                      </button>
                    </Tooltip>
                  </td>
                  <td className={mergeClasses(table.td, styles.clipCell)}>
                    <Tooltip content={row.projectName} relationship="description">
                      <span className={table.clip}>{row.projectName}</span>
                    </Tooltip>
                  </td>
                  <td className={mergeClasses(table.td, table.num, row.overdue && styles.late)}>{formatDate(row.due, "numeric")}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlanTable({
  styles,
  table,
  rows,
  onOpen,
}: {
  styles: ReturnType<typeof useStyles>;
  table: ReturnType<typeof useDataTableStyles>;
  rows: HomePlanSummaryRow[];
  onOpen: (id: string) => void;
}) {
  if (rows.length === 0) return <GhostEmptyState title="표시할 플랜이 없습니다" />;

  return (
    <div className={table.frame}>
      <table className={mergeClasses(table.table, styles.table)} aria-label="플랜">
        <thead>
          <tr>
            <th className={mergeClasses(table.th, styles.planNameCol)} scope="col">플랜</th>
            <th className={mergeClasses(table.th, table.num, styles.dueCol)} scope="col">종료일</th>
            <th className={mergeClasses(table.th, table.num, styles.pctCol)} scope="col">진척</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((plan) => (
            <tr key={plan.id} className={styles.row} onClick={() => onOpen(plan.id)}>
              <td className={mergeClasses(table.td, styles.clipCell)}>
                <Tooltip content={plan.level === "good" ? plan.name : `${plan.name} · ${plan.reason}`} relationship="description">
                  <button
                    type="button"
                    className={table.linkBtn}
                    aria-label={`${plan.name} 현황`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpen(plan.id);
                    }}
                  >
                    {plan.name}
                  </button>
                </Tooltip>
                {plan.level !== "good" ? <Caption1 className={styles.subline}>{plan.reason}</Caption1> : null}
              </td>
              <td className={mergeClasses(table.td, table.num)}>{formatDate(plan.finish, "numeric")}</td>
              <td className={mergeClasses(table.td, table.num)}>{plan.progress}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
